import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  GoogleMap, 
  Marker, 
  InfoWindow 
} from '@react-google-maps/api';
import { Box, Chip, Alert, Button, Typography, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { LocationOn, Refresh, Warning, CheckCircle, Error, GpsFixed,Edit } from '@mui/icons-material';
import { GPS_ACCURACY_THRESHOLD, getGPSAccuracySetting } from '../utils/utils';
import { useGoogleMaps } from '../utils/GoogleMapsProvider';
import { logAnalyticsEvent } from '../utils/analytics';

// Map container style
const mapContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '4px'
};

const AddressPicker = ({ 
  lat, 
  lng, 
  fetchAddressOnPointerChange, 
  setFormCoordinates,
  onLocationValidation,
  onAccuracyChange,
  // NEW: Accept GPS accuracy props from NewRequest
  initialGpsAccuracy = null,
  initialAccuracyStatus = 'unknown',
  initialLocationSource = 'manual',
  // NEW: Location services status from parent
  locationServicesEnabled = true
}) => {
  // State management
  const [center, setCenter] = useState({ lat: lat || 33.4484, lng: lng || -112.0740 });
  const [marker, setMarker] = useState({ lat: lat || 33.4484, lng: lng || -112.0740 });
  const [hasLocationPermission, setHasLocationPermission] = useState(false);
  const [locationAccuracy, setLocationAccuracy] = useState(initialGpsAccuracy);
  const [accuracyStatus, setAccuracyStatus] = useState(initialAccuracyStatus);
  const [isWithinServiceArea, setIsWithinServiceArea] = useState(true);
  const [locationSource, setLocationSource] = useState(initialLocationSource);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [showAccuracyWarning, setShowAccuracyWarning] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showInfoWindow, setShowInfoWindow] = useState(false);

  // Refs
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const { isLoaded, loadError } = useGoogleMaps();

  // Constants
  const ARIZONA_BOUNDS = {
    north: 37.00,
    south: 31.33,
    east: -109.05,
    west: -114.82
  };

  const ACCURACY_THRESHOLDS = {
    HIGH: GPS_ACCURACY_THRESHOLD,
    MEDIUM: 200,
    LOW: 300
  };

  // Map options - Clean mobile-friendly interface
  const mapOptions = {
    gestureHandling: isMobile ? 'greedy' : 'cooperative',
    mapTypeId: 'hybrid',
    
    mapTypeControl: true,
    mapTypeControlOptions: {
      position: window.google?.maps?.ControlPosition?.TOP_LEFT || 1,
      style: window.google?.maps?.MapTypeControlStyle?.DROPDOWN_MENU || 1,
      mapTypeIds: ['roadmap', 'satellite', 'hybrid', 'terrain']
    },
    
    zoomControl: false,
    streetViewControl: false,
    rotateControl: false,
    fullscreenControl: false,
    scaleControl: false,
    
    styles: [
      {
        featureType: 'poi.business',
        elementType: 'labels',
        stylers: [{ visibility: 'simplified' }]
      }
    ],
    
    disableDefaultUI: false,
    clickableIcons: false,
    keyboardShortcuts: false,
    tilt: 0,
    restriction: {
      latLngBounds: {
        north: 42.0,
        south: 28.0,
        east: -105.0,
        west: -118.0
      },
      strictBounds: false
    }
  };

  // Check if coordinates are within Arizona service area
  const isWithinArizona = useCallback((latitude, longitude) => {
    return latitude >= ARIZONA_BOUNDS.south && 
           latitude <= ARIZONA_BOUNDS.north && 
           longitude >= ARIZONA_BOUNDS.west && 
           longitude <= ARIZONA_BOUNDS.east;
  }, []);

  // Determine accuracy status
  const getAccuracyStatus = useCallback((accuracy) => {
    if (!accuracy) return 'unknown';
    if (accuracy <= ACCURACY_THRESHOLDS.HIGH) return 'high';
    if (accuracy <= ACCURACY_THRESHOLDS.MEDIUM) return 'medium';
    return 'low';
  }, []);

  // Get accuracy display configuration
  const getAccuracyDisplay = (status, accuracy) => {
    const configs = {
      high: { 
        color: 'success', 
        icon: <CheckCircle />, 
        text: `High (±${Math.round(accuracy)}m)`,
        description: 'GPS signal is strong and accurate'
      },
      medium: { 
        color: 'warning', 
        icon: <Warning />, 
        text: `Medium (±${Math.round(accuracy)}m)`,
        description: 'GPS accuracy is below requirement'
      },
      low: { 
        color: 'error', 
        icon: <Error />, 
        text: `Low (±${Math.round(accuracy)}m)`,
        description: 'GPS accuracy is poor'
      },
      unknown: { 
        color: 'default', 
        icon: <LocationOn />, 
        text: 'Manual Entry',
        description: 'Location entered manually'
      }
    };
    return configs[status] || configs.unknown;
  };

  // Check location services availability
  const checkLocationServices = useCallback(async () => {
    if (!navigator.geolocation) {
      return false;
    }
    
    try {
      if (navigator.permissions) {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        return result.state === 'granted';
      }
      return false;
    } catch (error) {
      console.error('Error checking location services:', error);
      return false;
    }
  }, []);

  // Update coordinates with validation
  const updateCoordinates = useCallback((newLat, newLng, source = 'manual', accuracy = null) => {
    console.log('📍 Updating coordinates:', { newLat, newLng, source, accuracy });
    
    const newCenter = { lat: newLat, lng: newLng };
    setCenter(newCenter);
    setMarker(newCenter);
    
    if (setFormCoordinates) {
      setFormCoordinates(newLat, newLng);
    }
    
    // Check service area
    const withinArea = isWithinArizona(newLat, newLng);
    setIsWithinServiceArea(withinArea);
    
    // Update accuracy if provided
    if (accuracy !== null) {
      setLocationAccuracy(accuracy);
      const status = getAccuracyStatus(accuracy);
      setAccuracyStatus(status);
      
      if (accuracy > GPS_ACCURACY_THRESHOLD) {
        setShowAccuracyWarning(true);
      } else {
        setShowAccuracyWarning(false);
      }
      
      if (onAccuracyChange) {
        onAccuracyChange(accuracy, status);
      }
    } else {
      setLocationAccuracy(null);
      setAccuracyStatus('unknown');
      setShowAccuracyWarning(false);
    }
    
    setLocationSource(source);
    
    // Notify parent about validation
    if (onLocationValidation) {
      onLocationValidation({
        coordinates: { lat: newLat, lng: newLng },
        withinServiceArea: withinArea,
        accuracy: accuracy,
        accuracyStatus: accuracy ? getAccuracyStatus(accuracy) : 'unknown',
        source: source,
        meetsAccuracyRequirement: accuracy ? accuracy <= GPS_ACCURACY_THRESHOLD : false
      });
    }
    
    // Fetch address
    if (fetchAddressOnPointerChange && typeof fetchAddressOnPointerChange === 'function') {
      fetchAddressOnPointerChange(newLat, newLng);
    }
  }, [setFormCoordinates, fetchAddressOnPointerChange, onLocationValidation, onAccuracyChange, isWithinArizona, getAccuracyStatus]);

  // NEW: Update accuracy when props change from NewRequest
  useEffect(() => {
    if (initialGpsAccuracy !== null && initialGpsAccuracy !== locationAccuracy) {
      setLocationAccuracy(initialGpsAccuracy);
      setAccuracyStatus(initialAccuracyStatus);
      setLocationSource(initialLocationSource);
      if (initialGpsAccuracy && initialLocationSource === 'gps') {
            setLocationSource('gps');  // THIS IS THE KEY LINE
        } else if (!initialGpsAccuracy) {
            setLocationSource('manual');
        }
      // Update warnings
      if (initialGpsAccuracy > GPS_ACCURACY_THRESHOLD) {
        setShowAccuracyWarning(true);
      } else {
        setShowAccuracyWarning(false);
      }

      
      // Trigger accuracy change callback
      if (onAccuracyChange) {
        onAccuracyChange(initialGpsAccuracy, initialAccuracyStatus);
      }
      
      // Trigger validation immediately
      if (onLocationValidation && lat && lng) {
        onLocationValidation({
          coordinates: { lat, lng },
          withinServiceArea: isWithinArizona(lat, lng),
          accuracy: initialGpsAccuracy,
          accuracyStatus: initialAccuracyStatus,
          source: initialLocationSource,
          meetsAccuracyRequirement: initialGpsAccuracy <= GPS_ACCURACY_THRESHOLD
        });
      }
    }
  }, [initialGpsAccuracy, initialAccuracyStatus, initialLocationSource, locationAccuracy, onLocationValidation, onAccuracyChange, lat, lng, isWithinArizona]);

  const handleMapClick = useCallback(async (event) => {
    // REMOVE the location services gate for manual pin drops
    // Pin drop IS the manual fallback — it shouldn't require GPS permission
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    updateCoordinates(lat, lng, 'manual');
    setShowInfoWindow(false);
}, [updateCoordinates]);
  // Handle marker drag
  const handleMarkerDragEnd = useCallback((event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    console.log('🎯 Marker dragged to:', lat, lng);
    setIsDragging(false);
    updateCoordinates(lat, lng, 'manual');
  }, [updateCoordinates]);

  const handleMarkerDragStart = useCallback(() => {
    setIsDragging(true);
    setShowInfoWindow(false);
  }, []);

  const handleMarkerClick = useCallback(() => {
    setShowInfoWindow(!showInfoWindow);
  }, [showInfoWindow]);

  // Enhanced GPS location with better error handling
  const handleGetCurrentLocation = useCallback(async () => {
    logAnalyticsEvent('manual_gps_request', {
    currentAccuracy: locationAccuracy,
    currentSource: locationSource
  });
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by this browser.");
      return;
    }

    setIsGettingLocation(true);
    console.log('🎯 Manual GPS location request...');
    
    // Check location services first
    const servicesEnabled = await checkLocationServices();
    if (!servicesEnabled) {
      setIsGettingLocation(false);
      // Trigger location services dialog
      if (onLocationValidation) {
        onLocationValidation({
          coordinates: null,
          withinServiceArea: false,
          accuracy: null,
          accuracyStatus: 'unknown',
          source: 'gps',
          meetsAccuracyRequirement: false,
          requiresLocationServices: true
        });
      }
      return;
    }
    
    const options = {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0
    };

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, options);
      });

      const { latitude, longitude, accuracy } = position.coords;
      console.log(`✅ Manual GPS Success: ${latitude}, ${longitude}, Accuracy: ±${accuracy}m`);
      logAnalyticsEvent('manual_gps_success', {
    accuracy: Math.round(accuracy),
    meetsThreshold: accuracy <= GPS_ACCURACY_THRESHOLD,
    latitude: latitude.toFixed(6),
    longitude: longitude.toFixed(6)
  });
  
 
      updateCoordinates(latitude, longitude, 'gps', accuracy);
      setHasLocationPermission(true);
      setLocationSource('gps'); 
      // Center map on new location
      if (mapRef.current) {
        mapRef.current.panTo({ lat: latitude, lng: longitude });
        mapRef.current.setZoom(18);
      }
      
    } catch (error) {
      console.error("Error getting location:", error);
      setHasLocationPermission(false);
      
      let errorMessage = "Unable to get your location. ";
      let requiresDialog = false;
      
      switch (error.code) {
        case error.PERMISSION_DENIED:
          errorMessage += "Please enable location permissions.";
          requiresDialog = true;
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage += "Location information is unavailable.";
          break;
        case error.TIMEOUT:
          errorMessage += "Location request timed out.";
          break;
        default:
          errorMessage += "An unknown error occurred.";
          break;
      }
       // In the error case:
  logAnalyticsEvent('manual_gps_error', {
    errorCode: error.code,
    errorMessage: errorMessage
  });
      if (requiresDialog && onLocationValidation) {
        onLocationValidation({
          coordinates: null,
          withinServiceArea: false,
          accuracy: null,
          accuracyStatus: 'unknown',
          source: 'gps',
          meetsAccuracyRequirement: false,
          requiresLocationServices: true
        });
      } else {
        alert(errorMessage);
      }
    } finally {
      setIsGettingLocation(false);
    }
  }, [updateCoordinates, checkLocationServices, onLocationValidation]);

  // Handle map load
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    console.log('🗺️ Map loaded successfully');
  }, []);

  // Update center when props change
  useEffect(() => {
    if (lat && lng) {
      const newCenter = { lat, lng };
      setCenter(newCenter);
      setMarker(newCenter);
    }
  }, [lat, lng]);

  // Hide Google Maps attribution clutter for clean mobile interface
  useEffect(() => {
    if (!isLoaded) return;
    
    const hideMapClutter = () => {
      const elementsToHide = [
        '.gm-style-cc',
        '.gmnoprint a[title="Report errors in the road map or imagery to Google"]',
        '.gmnoprint a[title="Click to see this area on Google Maps"]',
        '.gmnoprint a[title="Toggle fullscreen view"]',
        '.gmnoprint span',
        'button[title="Keyboard shortcuts"]'
      ];

      elementsToHide.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          if (element && element.style) {
            element.style.display = 'none';
          }
        });
      });

      const gmnoprint = document.querySelectorAll('.gmnoprint');
      gmnoprint.forEach(element => {
        if (!element.querySelector('.gm-style-mtc')) {
          element.style.display = 'none';
        }
      });
    };

    // Use MutationObserver instead of setInterval for better performance
    let observer;
    const setupObserver = () => {
      hideMapClutter(); // Initial cleanup
      
      observer = new MutationObserver((mutations) => {
        const hasNewElements = mutations.some(mutation => 
          mutation.addedNodes.length > 0
        );
        if (hasNewElements) {
          hideMapClutter();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    };

    setupObserver();

    return () => {
      if (observer) observer.disconnect();
    };
  }, [isLoaded]);

  // Check location permission on mount
  useEffect(() => {
    const checkLocationPermissionOnMount = async () => {
      try {
        if (navigator.permissions) {
          const result = await navigator.permissions.query({ name: 'geolocation' });
          setHasLocationPermission(result.state === 'granted');
          
          result.addEventListener('change', () => {
            setHasLocationPermission(result.state === 'granted');
          });
        }
      } catch (error) {
        console.error('Error checking location permissions:', error);
      }
    };
    
    checkLocationPermissionOnMount();
  }, []);

  // Get marker icon based on status
  const getMarkerIcon = () => {
    let color = '#6B7280'; // Default gray
    
    if (!isWithinServiceArea) {
      color = '#EF4444'; // Red for outside service area
    } else {
      switch (accuracyStatus) {
        case 'high': color = '#22C55E'; break; // Green
        case 'medium': color = '#F59E0B'; break; // Yellow
        case 'low': color = '#EF4444'; break; // Red
        default: color = '#6B7280'; break; // Gray
      }
    }

    return {
      path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
      fillColor: color,
      fillOpacity: 0.8,
      strokeColor: '#FFFFFF',
      strokeWeight: 2,
      scale: 8
    };
  };

  if (loadError) {
    return (
      <Alert severity="error">
        <Typography variant="body2">
          Error loading Google Maps. Please check your internet connection and try again.
        </Typography>
      </Alert>
    );
  }

  if (!isLoaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading map...
        </Typography>
      </Box>
    );
  }

  const accuracyDisplay = getAccuracyDisplay(accuracyStatus, locationAccuracy);

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Status Bar */}
     {/* Single Unified Status Ribbon */}
<Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {/* Status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Status:
            </Typography>
            <Chip
                icon={locationSource === 'gps' ? <GpsFixed /> : <Edit />}
                label={locationSource === 'gps' ? 'GPS Location' : 'Manual Entry'}
                color={locationSource === 'gps' ? 'success' : 'default'}
                size="small"
            />
        </Box>
        
        {/* Accuracy */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Accuracy:
            </Typography>
            <Typography variant="body2">
                {locationAccuracy ? `±${Math.round(locationAccuracy)}m` : 'Not Available'}
            </Typography>
        </Box>
    </Box>
</Box>

      {/* Map Container */}
      <Box sx={{ 
        position: 'relative',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden'
      }}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={locationServicesEnabled ? 16 : 10} 
          options={mapOptions}
          onClick={handleMapClick}
          onLoad={onMapLoad}
        >
          {/* Draggable Marker */}
          <Marker
            position={marker}
            draggable={true}
            onDragStart={handleMarkerDragStart}
            onDragEnd={handleMarkerDragEnd}
            onClick={handleMarkerClick}
            icon={getMarkerIcon()}
            ref={markerRef}
          />

          {/* Info Window */}
          {showInfoWindow && (
            <InfoWindow
              position={marker}
              onCloseClick={() => setShowInfoWindow(false)}
            >
              <Box sx={{ p: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Selected Location
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Lat: {marker.lat.toFixed(6)}, Lng: {marker.lng.toFixed(6)}
                </Typography>
                {locationAccuracy && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    Accuracy: ±{Math.round(locationAccuracy)}m
                  </Typography>
                )}
                <Typography variant="caption" display="block" color="primary.main">
                  Source: {locationSource === 'gps' ? 'GPS' : 'Manual'}
                </Typography>
              </Box>
            </InfoWindow>
          )}
        </GoogleMap>

        {/* GPS Button */}
        <Box sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 10,
        }}>
          <Tooltip title="Get Current GPS Location">
            <IconButton
              onClick={handleGetCurrentLocation}
              disabled={isGettingLocation}
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '&:disabled': {
                  backgroundColor: 'grey.400',
                },
                boxShadow: 2,
              }}
            >
              {isGettingLocation ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <GpsFixed />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Mobile Instructions */}
        {isMobile && (
          <Box sx={{
            position: 'absolute',
            bottom: 10,
            left: 10,
            right: 10,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: 1,
            borderRadius: 1,
            zIndex: 10,
          }}>
            <Typography variant="caption" sx={{ textAlign: 'center', display: 'block' }}>
              Tap map to set location • Drag marker to adjust • Use GPS button for current location
            </Typography>
          </Box>
        )}
      </Box>

      {/* Custom CSS for mobile-optimized map */}
      <style jsx global>{`
        .gm-style-mtc {
          margin: 8px !important;
        }
        
        .gm-style-mtc > div {
          background-color: rgba(255, 255, 255, 0.95) !important;
          border-radius: 6px !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
        }
        
        .gm-style-mtc button {
          font-size: 13px !important;
          padding: 6px 12px !important;
          border-radius: 4px !important;
        }
        
        .gm-style-cc,
        .gmnoprint span:not(.gm-style-mtc span),
        .gmnoprint a[href*="maps.google.com"],
        .gmnoprint a[title*="report"],
        .gmnoprint a[title*="Report"],
        .gmnoprint a[title*="keyboard"],
        .gmnoprint a[title*="Keyboard"],
        .gmnoprint div[style*="font-family"] {
          display: none !important;
        }
        
        .gm-style {
          padding-bottom: 0 !important;
        }
        
        .gmnoprint:not(:has(.gm-style-mtc)) {
          display: none !important;
        }
      `}</style>
    </Box>
  );
};

export default AddressPicker;